import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import ColorPicker from '../../../components/ColorPicker';
import StaffNotFound from '../StaffNotFound';
import Banner from '../../../components/Banner';
import AskFrank from '../../AskFrank';
import FranklyNetBanner from '../../../components/franklynet/FranklyNetBanner';
import FullNavigator from '../../../components/FullNavigator';
import { DEFAULT_THEME, mergeThemeConfig, resolveThemeAssetUrl } from '../../../data/siteTheme';
import {
    getAdminSiteThemes,
    createSiteTheme,
    updateSiteTheme,
    deleteSiteTheme,
    activateSiteTheme,
    uploadSiteThemeAsset,
    listSiteThemeAssets,
    deleteSiteThemeAsset
} from '../../../services/api';

const LOGO_SLOTS = [
    { key: 'logo', tab: 'website', label: 'Website logo', hint: 'The red banner on every page, and the Staff Panel bar.' },
    { key: 'askFrankLogo', tab: 'askfrank', label: 'AskFrank logo', hint: 'Shown on AskFrank and FranklyNet.' }
];

const KEY_PATTERN = /^[a-z0-9]{2,24}$/;

const input = 'w-full border border-gray-300 bg-white px-2 py-[5px] text-[13px] text-gray-800 outline-none focus:border-maroon disabled:bg-gray-100 disabled:text-gray-500';
const label = "block font-['Jersey_10'] text-[14px] uppercase tracking-[1px] text-maroon";
const btn = "font-['Jersey_10'] uppercase text-[15px] tracking-[1px] px-3 py-2 rounded transition-all";
const shadow = { boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.2)' };

const emptyForm = () => ({
    id: null,
    key: '',
    name: '',
    isDefault: false,
    config: mergeThemeConfig(DEFAULT_THEME.config)
});

const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const assetUrlsOf = (config, tab) => {
    const urls = LOGO_SLOTS.filter(slot => slot.tab === tab).map(slot => config[slot.key]?.url);

    if (tab === 'askfrank') {
        urls.push(config.askFrankTile?.url);
    } else {
        urls.push(config.bannerTile?.url);
        (config.decorations || []).forEach(d => urls.push(d.url));
    }

    return [...new Set(urls.filter(Boolean))];
};

// Accepts what people actually type: with or without the hash, upper or lower
// case, and the 3-digit shorthand. Anything else is not a colour yet.
const normaliseHex = (raw) => {
    const body = String(raw ?? '').trim().replace(/^#/, '');

    if (/^[0-9a-fA-F]{6}$/.test(body)) return `#${body.toLowerCase()}`;
    if (/^[0-9a-fA-F]{3}$/.test(body)) return `#${body.split('').map(c => c + c).join('').toLowerCase()}`;

    return null;
};

// The field keeps its own text so a half-typed "#8a" does not reach the config
// and blank the furni. Only a complete colour, or an empty box, is committed.
function HexField({ value, disabled, onChange }) {
    const [text, setText] = useState(value || '');

    // Follow the picker, the reset button and theme switches, but never
    // overwrite something that already normalises to the committed value.
    useEffect(() => {
        setText(prev => (normaliseHex(prev) === value ? prev : (value || '')));
    }, [value]);

    const handle = (raw) => {
        setText(raw);

        if (!raw.trim()) return onChange(null);

        const hex = normaliseHex(raw);
        if (hex) onChange(hex);
    };

    return (
        <input
            className={input}
            value={text}
            disabled={disabled}
            placeholder="#8b2e2e"
            spellCheck={false}
            onChange={(e) => handle(e.target.value)}
        />
    );
}

// Sits inside the colour swatch so it reads as a picker, not a flat block.
function EyedropperIcon() {
    return (
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" style={{ filter: 'drop-shadow(0 1px 0 rgba(0,0,0,0.6))' }}>
            <path
                d="M17.2 2.8a2.7 2.7 0 0 1 3.8 3.8l-1.6 1.6 1 1a1 1 0 0 1-1.4 1.4l-1-1-7.2 7.2a3 3 0 0 1-1.5.8l-3.6.8-1.2 1.2a1 1 0 0 1-1.4-1.4l1.2-1.2.8-3.6a3 3 0 0 1 .8-1.5l7.2-7.2-1-1A1 1 0 0 1 13.6 3l1 1 1.6-1.6Z"
                fill="#ffffff"
                stroke="rgba(0,0,0,0.55)"
                strokeWidth="1"
            />
        </svg>
    );
}

function Panel({ title, children, right }) {
    return (
        <section className="border border-gray-300 bg-offwhite" style={{ boxShadow: '0 3px 0 0 rgba(0,0,0,0.5)' }}>
            <header className="flex items-center justify-between gap-2 bg-maroon px-3 py-[6px]">
                <h3 className="font-['Jersey_10'] text-[16px] uppercase tracking-[1px] text-white">{title}</h3>
                {right}
            </header>
            <div className="p-3">{children}</div>
        </section>
    );
}

function NumberField({ title, value, onChange, min, max, disabled, suffix = 'px' }) {
    return (
        <label className="block">
            <span className={label}>{title}</span>
            <div className="flex items-center gap-2">
                <input
                    type="number"
                    min={min}
                    max={max}
                    step={1}
                    className={input}
                    value={value}
                    disabled={disabled}
                    onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))}
                />
                <span className="shrink-0 text-[12px] text-gray-500">{suffix}</span>
            </div>
        </label>
    );
}

function ImageSlot({ value, onChange, onPick, placeholder, disabled, uploading }) {
    return (
        <div className="flex items-center gap-2">
            <div
                className="h-[46px] w-[64px] shrink-0 border border-gray-300 bg-[#e5e7eb]"
                style={value ? {
                    backgroundImage: `url(${resolveThemeAssetUrl(value)})`,
                    backgroundSize: 'contain',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    imageRendering: 'pixelated'
                } : undefined}
            />
            <div className="min-w-0 flex-1">
                <input
                    className={input}
                    value={value || ''}
                    disabled={disabled}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                />
            </div>
            <button
                type="button"
                onClick={onPick}
                disabled={disabled || uploading}
                className={`${btn} shrink-0 bg-gray-200 text-gray-800 hover:bg-gray-300 disabled:opacity-60`}
                style={shadow}
            >
                {uploading ? '...' : 'Upload'}
            </button>
        </div>
    );
}

// Every preview mounts the real components against the draft config, so what is
// on screen is a render of the page rather than a mock-up of it. STAGE_WIDTH is
// a desktop viewport; the stage is drawn at that width and then scaled to fit
// whatever room the panel has.
const STAGE_WIDTH = 1400;
const STAGE_BODY = 70;

const noop = () => {};

// The recommended tiles come from the live AskFrank front page, which the theme
// has no say over. These stand in so the strip has its real shape.
const SAMPLE_TILES = Array.from({ length: 6 }, (unused, i) => ({
    id: `sample${i}`,
    title: 'Recommended',
    subtitle: 'Sample tile',
    url: '#'
}));

// Scales its children down and clips to the scaled box. Height is measured
// rather than declared: the navigator sizes itself, and the banner's furni and
// avatars load in after the first paint. pointerEvents none keeps the render
// inert; anything draggable opts back in.
function Stage({ scale, initialHeight, children }) {
    const stageRef = useRef(null);
    const [height, setHeight] = useState(initialHeight);

    useEffect(() => {
        const el = stageRef.current;
        if (!el) return;

        const measure = () => setHeight(el.offsetHeight);
        measure();

        if (typeof ResizeObserver === 'undefined') return;

        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <div style={{ width: STAGE_WIDTH * scale, height: height * scale, overflow: 'hidden' }}>
            <div
                ref={stageRef}
                className="relative bg-coal_dark bg-repeat"
                style={{
                    width: STAGE_WIDTH,
                    transform: `scale(${scale})`,
                    transformOrigin: 'top left',
                    pointerEvents: 'none'
                }}
            >
                {children}
            </div>
        </div>
    );
}

function WebsiteStage({ config, scale, selectedId, onGrab }) {
    return (
        <Stage scale={scale} initialHeight={config.bannerHeight + 280}>
            <Banner
                themeOverride={{ config }}
                decorationProps={{ interactive: true, forceVisible: true, selectedId, onGrab }}
            />

            <FullNavigator
                userData={null}
                activeSection="news"
                setActiveSection={noop}
                activePath="/articles/all"
                setActivePath={noop}
            />

            <div style={{ height: STAGE_BODY }} />
        </Stage>
    );
}

// The AskFrank landing page tiles its own background, which is why that grid is
// a separate input from the banner tile.
function AskFrankStage({ config, scale }) {
    return (
        <Stage scale={scale} initialHeight={720}>
            <AskFrank preview themeOverride={{ config }} />
        </Stage>
    );
}

// The FranklyNet search strip, on the page background it sits against. The query
// is left empty on purpose: the suggestions dropdown only fetches once there is
// something to complete, so an empty box keeps the preview from hitting the API.
function FranklyNetStage({ config, scale }) {
    return (
        <Stage scale={scale} initialHeight={420}>
            <FranklyNetBanner query="" recommended={SAMPLE_TILES} onSearch={noop} themeOverride={{ config }} />

            <div className="w-full bg-white/5" style={{ height: 320 }}>
                <div className="mx-auto flex w-full max-w-[1400px] items-center justify-center px-4 py-5" style={{ height: '100%' }}>
                    <span className="font-['Jersey_10'] text-[18px] uppercase tracking-[1px] text-white/30">Search results</span>
                </div>
            </div>
        </Stage>
    );
}

const PREVIEW_TABS = [
    { key: 'website', label: 'Website' },
    { key: 'askfrank', label: 'AskFrank' }
];

const ASKFRANK_VIEWS = [
    { key: 'landing', label: 'Landing' },
    { key: 'franklynet', label: 'FranklyNet' }
];

function BannerPreview({ config, tab, onTabChange, selectedId, onSelect, onMove }) {
    const holderRef = useRef(null);
    const [fit, setFit] = useState(false);
    const [askFrankView, setAskFrankView] = useState('landing');
    const [available, setAvailable] = useState(STAGE_WIDTH);
    const dragRef = useRef(null);

    useEffect(() => {
        const el = holderRef.current;
        if (!el) return;

        const measure = () => setAvailable(el.clientWidth || STAGE_WIDTH);
        measure();

        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', measure);
            return () => window.removeEventListener('resize', measure);
        }

        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const scale = fit ? Math.min(1, available / STAGE_WIDTH) : 1;

    // Pointer movement is in screen pixels; the stage may be scaled down, so
    // undo the scale before it becomes an offset the visitor will see.
    useEffect(() => {
        const onMouseMove = (event) => {
            const drag = dragRef.current;
            if (!drag) return;
            event.preventDefault();

            onMove(drag.id, {
                offsetX: Math.round(drag.startX + (event.clientX - drag.mouseX) / drag.scale),
                offsetY: Math.round(drag.startY + (event.clientY - drag.mouseY) / drag.scale)
            });
        };

        const onMouseUp = () => { dragRef.current = null; };

        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };
    }, [onMove]);

    const grab = (event, decoration) => {
        event.preventDefault();
        onSelect(decoration.id);
        dragRef.current = {
            id: decoration.id,
            startX: decoration.offsetX,
            startY: decoration.offsetY,
            mouseX: event.clientX,
            mouseY: event.clientY,
            scale
        };
    };

    return (
        <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-1 border-b border-gray-300">
                {PREVIEW_TABS.map(preview => (
                    <button
                        key={preview.key}
                        type="button"
                        onClick={() => onTabChange(preview.key)}
                        className={`-mb-px border border-b-0 px-4 py-2 font-['Jersey_10'] text-[16px] uppercase tracking-[1px] transition-all
                            ${tab === preview.key
                                ? 'border-gray-300 bg-offwhite text-maroon'
                                : 'border-transparent bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                    >
                        {preview.label}
                    </button>
                ))}
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <button
                    type="button"
                    onClick={() => setFit(false)}
                    className={`${btn} ${fit ? 'bg-gray-200 text-gray-800 hover:bg-gray-300' : 'bg-maroon text-white'}`}
                    style={shadow}
                >
                    100%
                </button>
                <button
                    type="button"
                    onClick={() => setFit(true)}
                    className={`${btn} ${fit ? 'bg-maroon text-white' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
                    style={shadow}
                >
                    Fit
                </button>
                {tab === 'askfrank' && (
                    <>
                        <span className="mx-1 h-[24px] w-px bg-gray-300" />
                        {ASKFRANK_VIEWS.map(view => (
                            <button
                                key={view.key}
                                type="button"
                                onClick={() => setAskFrankView(view.key)}
                                className={`${btn} ${askFrankView === view.key ? 'bg-maroon text-white' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
                                style={shadow}
                            >
                                {view.label}
                            </button>
                        ))}
                    </>
                )}

                <span className="text-[12px] text-gray-600">
                    {STAGE_WIDTH}px wide desktop at {Math.round(scale * 100)}%.
                    {tab === 'website' ? ' Drag any extra asset to place it.' : ' Both AskFrank surfaces share the one grid.'}
                </span>
            </div>

            <div ref={holderRef} className={`border border-gray-400 ${fit ? '' : 'overflow-x-auto'}`}>
                {tab === 'website' ? (
                    <WebsiteStage config={config} scale={scale} selectedId={selectedId} onGrab={grab} />
                ) : askFrankView === 'franklynet' ? (
                    <FranklyNetStage config={config} scale={scale} />
                ) : (
                    <AskFrankStage config={config} scale={scale} />
                )}
            </div>
        </div>
    );
}

function SiteThemes({ userData }) {
    const [themes, setThemes] = useState([]);
    const [activeKey, setActiveKey] = useState('default');
    const [loading, setLoading] = useState(true);
    const [selectedId, setSelectedId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [uploadingTo, setUploadingTo] = useState(null);
    const [assets, setAssets] = useState([]);
    const [selectedDecoration, setSelectedDecoration] = useState(null);
    // Shared with the preview: the tab decides which half of the theme the
    // editor shows, so the fields on screen always match the render above them.
    const [tab, setTab] = useState('website');

    const fileInputRef = useRef(null);
    const uploadTargetRef = useRef(null);

    const isCreating = selectedId === 'new';
    const readOnly = form.isDefault;

    const load = useCallback(async (keepId = null) => {
        setLoading(true);
        try {
            const res = await getAdminSiteThemes();
            const list = Array.isArray(res?.themes) ? res.themes : [];
            setThemes(list.map(theme => ({ ...theme, config: mergeThemeConfig(theme.config) })));
            setActiveKey(res?.activeKey || 'default');

            if (keepId !== null) {
                const found = list.find(t => t.id === keepId);
                if (found) {
                    setSelectedId(found.id);
                    setForm({ id: found.id, key: found.key, name: found.name, isDefault: found.isDefault, config: mergeThemeConfig(found.config) });
                    setDirty(false);
                }
            }
        } catch (error) {
            toast.error('Could not load site themes');
        } finally {
            setLoading(false);
        }
    }, []);

    const loadAssets = useCallback(async () => {
        try {
            const res = await listSiteThemeAssets();
            setAssets(Array.isArray(res?.files) ? res.files : []);
        } catch {
            setAssets([]);
        }
    }, []);

    useEffect(() => {
        load();
        loadAssets();
    }, [load, loadAssets]);

    const openTheme = (theme) => {
        setSelectedId(theme.id);
        setForm({ id: theme.id, key: theme.key, name: theme.name, isDefault: theme.isDefault, config: mergeThemeConfig(theme.config) });
        setDirty(false);
    };

    const startNew = (from = null) => {
        const source = from || themes.find(t => t.isDefault) || DEFAULT_THEME;
        setSelectedId('new');
        setForm({
            id: null,
            key: '',
            name: from ? `${from.name} copy` : '',
            isDefault: false,
            config: mergeThemeConfig(source.config)
        });
        setDirty(true);
    };

    const setConfig = (patch) => {
        setForm(prev => ({ ...prev, config: { ...prev.config, ...patch } }));
        setDirty(true);
    };

    const setSlot = (slotKey, patch) => {
        setForm(prev => ({
            ...prev,
            config: { ...prev.config, [slotKey]: { ...prev.config[slotKey], ...patch } }
        }));
        setDirty(true);
    };

    const setDecoration = (index, patch) => {
        setForm(prev => ({
            ...prev,
            config: {
                ...prev.config,
                decorations: prev.config.decorations.map((d, i) => (i === index ? { ...d, ...patch } : d))
            }
        }));
        setDirty(true);
    };

    // Functional update: an upload resolves a tick or two after the click, and
    // the list must not be rebuilt from a stale copy. A new piece lands clear of
    // the logo rather than at 0,0 underneath it, so it is there to be dragged.
    const addDecoration = (url = '') => {
        const id = `d${Date.now()}`;

        setForm(prev => ({
            ...prev,
            config: {
                ...prev.config,
                decorations: [
                    ...(prev.config.decorations || []),
                    { id, url, offsetX: 260, offsetY: 0, zIndex: 7, width: null, desktopOnly: true, anchorRight: false, label: '' }
                ]
            }
        }));

        setSelectedDecoration(id);
        setDirty(true);
    };

    const removeDecoration = (index) => {
        setConfig({ decorations: form.config.decorations.filter((_, i) => i !== index) });
    };

    // The preview drags by id: the list index means nothing to it, and the
    // functional update keeps every mousemove working off the latest offsets.
    const moveDecoration = useCallback((id, patch) => {
        setForm(prev => ({
            ...prev,
            config: {
                ...prev.config,
                decorations: prev.config.decorations.map(d => (d.id === id ? { ...d, ...patch } : d))
            }
        }));
        setDirty(true);
    }, []);

    const pickFile = (target) => {
        uploadTargetRef.current = target;
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
            fileInputRef.current.click();
        }
    };

    const onFileChosen = async (event) => {
        const file = event.target.files?.[0];
        const target = uploadTargetRef.current;
        if (!file || !target) return;

        setUploadingTo(target.kind);
        const res = await uploadSiteThemeAsset(file).catch(e => ({ success: false, message: e?.message }));
        setUploadingTo(null);

        if (!res?.success || !res.url) {
            toast.error(res?.message || 'Upload failed', { autoClose: 7000 });
            return;
        }

        if (target.kind === 'decoration') setDecoration(target.index, { url: res.url });
        else if (target.kind === 'newDecoration') addDecoration(res.url);
        else setSlot(target.kind, { url: res.url });

        toast.success('Asset uploaded');
        loadAssets();
    };

    const save = async () => {
        const name = form.name.trim();
        if (!name) {
            toast.error('Give the theme a name');
            return;
        }

        const payload = { name, config: form.config };

        setSaving(true);
        let res;
        if (isCreating) {
            const key = form.key.trim().toLowerCase();
            if (!KEY_PATTERN.test(key)) {
                setSaving(false);
                toast.error('Key must be 2 to 24 characters, lowercase letters and numbers only');
                return;
            }
            res = await createSiteTheme({ ...payload, key }).catch(e => ({ success: false, message: e?.message }));
        } else {
            res = await updateSiteTheme(form.id, payload).catch(e => ({ success: false, message: e?.message }));
        }
        setSaving(false);

        if (res?.success) {
            toast.success(res.message || 'Saved');
            setDirty(false);
            await load(isCreating ? res.id : form.id);
        } else {
            toast.error(res?.message || 'Could not save the theme', { autoClose: 7000 });
        }
    };

    const remove = async () => {
        if (!window.confirm(`Delete "${form.name}"? Uploaded assets stay in the library.`)) return;

        const res = await deleteSiteTheme(form.id).catch(e => ({ success: false, message: e?.message }));

        if (res?.success) {
            toast.success('Theme deleted');
            setSelectedId(null);
            setForm(emptyForm());
            setDirty(false);
            await load();
        } else {
            toast.error(res?.message || 'Could not delete the theme', { autoClose: 7000 });
        }
    };

    const goLive = async (key) => {
        if (dirty && !window.confirm('You have unsaved changes. Going live reloads the panel and loses them. Continue?')) return;

        const res = await activateSiteTheme(key).catch(e => ({ success: false, message: e?.message }));

        if (!res?.success) {
            toast.error(res?.message || 'Could not switch theme', { autoClose: 7000 });
            return;
        }

        setActiveKey(res.activeKey || key);
        toast.success(res.message || 'Theme is live');

        // The theme is fetched once at boot, so the Staff Panel banner is still
        // wearing the old one. Reload so the switch is visible straight away.
        setTimeout(() => window.location.reload(), 700);
    };

    const removeAsset = async (filename) => {
        if (!window.confirm(`Delete ${filename} from the asset library?`)) return;

        const res = await deleteSiteThemeAsset(filename).catch(e => ({ success: false, message: e?.message }));

        if (res?.success) {
            toast.success('Asset deleted');
            loadAssets();
        } else {
            toast.error(res?.message || 'Could not delete the asset', { autoClose: 7000 });
        }
    };

    // Logos and tiles live in the same upload folder as the art, so anything a
    // theme uses in one of those slots is filtered out of the art library. The
    // unsaved draft counts too, or a logo you just uploaded would show up here.
    const slotUrls = new Set();
    for (const theme of [...themes, form]) {
        slotUrls.add(theme.config?.logo?.url);
        slotUrls.add(theme.config?.askFrankLogo?.url);
        slotUrls.add(theme.config?.bannerTile?.url);
        slotUrls.add(theme.config?.askFrankTile?.url);
    }
    const artAssets = assets.filter(asset => !slotUrls.has(asset.url));

    if (!userData || !userData.hasAdminRole) return <StaffNotFound inline />;

    const defaultTheme = themes.find(t => t.isDefault) || DEFAULT_THEME;
    const downloads = assetUrlsOf(defaultTheme.config, tab);

    return (
        <div className="flex flex-col gap-3">
            <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/gif,image/webp,image/jpeg"
                className="hidden"
                onChange={onFileChosen}
            />

            <Panel
                title="Site themes"
                right={
                    <button
                        type="button"
                        onClick={() => startNew()}
                        className="font-['Jersey_10'] text-[14px] uppercase tracking-[1px] text-gold hover:underline"
                    >
                        + New theme
                    </button>
                }
            >
                {loading ? (
                    <p className="text-[13px] text-gray-600">Loading...</p>
                ) : (
                    <div className="flex flex-col gap-1">
                        {themes.map(theme => {
                            const selected = selectedId === theme.id;
                            const live = theme.key === activeKey;
                            return (
                                <div key={theme.id} className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => openTheme(theme)}
                                        className={`flex min-w-0 flex-1 items-center gap-3 rounded px-2 py-[6px] text-left transition-all
                                            ${selected ? 'bg-maroon text-white' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
                                        style={shadow}
                                    >
                                        <span
                                            className="h-[34px] w-[60px] shrink-0 border border-black/20 bg-[#e5e7eb]"
                                            style={{
                                                backgroundImage: `url(${resolveThemeAssetUrl(theme.config.bannerTile.url)})`,
                                                backgroundRepeat: 'repeat',
                                                imageRendering: 'pixelated'
                                            }}
                                        />
                                        <span className="min-w-0 flex-1">
                                            <span className="block truncate font-['Jersey_10'] text-[16px] uppercase tracking-[1px]">{theme.name}</span>
                                            <span className={`block truncate text-[11px] ${selected ? 'text-white/70' : 'text-gray-500'}`}>
                                                {theme.key} · {(theme.config.decorations || []).length} extra assets
                                            </span>
                                        </span>
                                        {theme.isDefault && (
                                            <span className="shrink-0 font-['Jersey_10'] text-[12px] uppercase tracking-[1px] text-gold">locked</span>
                                        )}
                                    </button>

                                    {live ? (
                                        <span
                                            className="shrink-0 rounded bg-green-600 px-3 py-2 font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-white"
                                            style={shadow}
                                        >
                                            Live
                                        </span>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => goLive(theme.key)}
                                            className={`${btn} shrink-0 bg-green-100 text-green-900 hover:bg-green-200`}
                                            style={shadow}
                                        >
                                            Go live
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                <p className="mt-3 text-[13px] text-gray-600">
                    The live theme is stored in Settings, next to Maintenance Mode. Switching takes effect on the visitor's next page load.
                </p>
            </Panel>

            {selectedId === null ? (
                <Panel title="Editor">
                    <p className="text-[14px] text-gray-700">
                        Pick a theme above to edit it, or start a new one. New themes begin as a copy of the default, so you only change
                        what the season actually needs.
                    </p>
                </Panel>
            ) : (
                <>
                <Panel title="Banner preview">
                    <BannerPreview
                        config={form.config}
                        tab={tab}
                        onTabChange={setTab}
                        selectedId={selectedDecoration}
                        onSelect={setSelectedDecoration}
                        onMove={readOnly ? () => {} : moveDecoration}
                    />
                    <p className="mt-2 text-[12px] text-gray-600">
                        Both tabs render the real components against your unsaved draft, so what you see is what the visitor gets.
                        Extra assets are placed from the logo's top-left corner and can hang past the banner onto the navigation bar;
                        drag one on the Website tab, or type exact offsets below.
                    </p>
                </Panel>

                <div className="grid grid-cols-1 gap-3 xl:grid-cols-12">
                    <div className="xl:col-span-7">
                        <div className="flex flex-col gap-3">
                            {tab === 'website' && (
                            <Panel
                                title="Extra assets"
                                right={
                                    !readOnly && (
                                        <button
                                            type="button"
                                            onClick={() => pickFile({ kind: 'newDecoration' })}
                                            className="font-['Jersey_10'] text-[14px] uppercase tracking-[1px] text-gold hover:underline"
                                        >
                                            + Upload and place
                                        </button>
                                    )
                                }
                            >
                                <p className="mb-3 text-[13px] text-gray-700">
                                    The stylised extras a season hangs on the site: cobwebs, bunting, snow on the banner. Each one is
                                    placed from the logo's top-left corner, so drag it on the preview above or type exact offsets.
                                    Nothing is clipped, so a big enough Y drops a piece over the navigation bar.
                                </p>

                                {readOnly ? (
                                    <p className="text-[13px] text-gray-600">
                                        The default theme carries none, and cannot be given any. Duplicate it to build a season on top.
                                    </p>
                                ) : (
                                <>
                                <div className="mb-3">
                                    <span className={label}>Uploaded library</span>
                                    <span className="mb-1 block text-[11px] text-gray-500">
                                        Shared by every theme, so art can be reused. Logos and background tiles are not listed here.
                                    </span>
                                    {artAssets.length === 0 ? (
                                        <p className="text-[13px] text-gray-600">
                                            Nothing uploaded yet. PNG, GIF, WebP or JPEG, up to 3MB.
                                        </p>
                                    ) : (
                                        <div className="flex gap-2 overflow-x-auto pb-1">
                                            {artAssets.map(asset => (
                                                <div key={asset.filename} className="w-[116px] shrink-0 border border-gray-300 bg-white p-1">
                                                    <span
                                                        className="block h-[54px] w-full border border-black/10 bg-[#f3f4f6]"
                                                        style={{
                                                            backgroundImage: `url(${resolveThemeAssetUrl(asset.url)})`,
                                                            backgroundSize: 'contain',
                                                            backgroundPosition: 'center',
                                                            backgroundRepeat: 'no-repeat',
                                                            imageRendering: 'pixelated'
                                                        }}
                                                    />
                                                    <span className="mt-1 block truncate text-[11px] text-gray-700" title={asset.filename}>
                                                        {asset.filename}
                                                    </span>
                                                    <span className="block text-[10px] text-gray-500">{formatSize(asset.size)}</span>
                                                    <div className="mt-1 flex items-center justify-between gap-1">
                                                        {readOnly ? (
                                                            <a
                                                                href={resolveThemeAssetUrl(asset.url)}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="text-[11px] text-gray-600 hover:text-maroon"
                                                            >
                                                                open
                                                            </a>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                onClick={() => addDecoration(asset.url)}
                                                                className="font-['Jersey_10'] text-[12px] uppercase tracking-[1px] text-maroon hover:underline"
                                                            >
                                                                place
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            onClick={() => removeAsset(asset.filename)}
                                                            className="text-[11px] text-red-600 hover:underline"
                                                        >
                                                            delete
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="flex items-center justify-between">
                                    <span className={label}>On this theme</span>
                                    {!readOnly && (
                                        <button
                                            type="button"
                                            onClick={() => addDecoration()}
                                            className="font-['Jersey_10'] text-[13px] uppercase tracking-[1px] text-gold hover:underline"
                                        >
                                            + Blank row
                                        </button>
                                    )}
                                </div>

                                {(form.config.decorations || []).length === 0 ? (
                                    <p className="mt-1 text-[13px] text-gray-600">
                                        None yet. Upload one above, or press "place" on something already in the library.
                                    </p>
                                ) : (
                                    <div className="mt-2 flex flex-col gap-3">
                                        {form.config.decorations.map((decoration, index) => (
                                            <div
                                                key={decoration.id}
                                                onMouseDown={() => setSelectedDecoration(decoration.id)}
                                                className={`border bg-white p-2 ${selectedDecoration === decoration.id ? 'border-gold' : 'border-gray-300'}`}
                                            >
                                                <ImageSlot
                                                    value={decoration.url}
                                                    onChange={(v) => setDecoration(index, { url: v })}
                                                    onPick={() => pickFile({ kind: 'decoration', index })}
                                                    uploading={uploadingTo === 'decoration'}
                                                    disabled={readOnly}
                                                    placeholder="/themeassets/cobweb.png"
                                                />

                                                <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                                                    <NumberField
                                                        title="X"
                                                        value={decoration.offsetX}
                                                        min={-2000}
                                                        max={2000}
                                                        disabled={readOnly}
                                                        onChange={(v) => setDecoration(index, { offsetX: v })}
                                                    />
                                                    <NumberField
                                                        title="Y"
                                                        value={decoration.offsetY}
                                                        min={-2000}
                                                        max={2000}
                                                        disabled={readOnly}
                                                        onChange={(v) => setDecoration(index, { offsetY: v })}
                                                    />
                                                    <NumberField
                                                        title="Layer"
                                                        value={decoration.zIndex}
                                                        min={0}
                                                        max={60}
                                                        suffix=""
                                                        disabled={readOnly}
                                                        onChange={(v) => setDecoration(index, { zIndex: v })}
                                                    />
                                                    <label className="block">
                                                        <span className={label}>Width</span>
                                                        <input
                                                            type="number"
                                                            min={1}
                                                            max={4000}
                                                            className={input}
                                                            value={decoration.width ?? ''}
                                                            disabled={readOnly}
                                                            placeholder="auto"
                                                            onChange={(e) => setDecoration(index, { width: e.target.value === '' ? null : Number(e.target.value) })}
                                                        />
                                                    </label>
                                                </div>

                                                <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                                                    <label className="flex items-center gap-2 text-[13px] text-gray-700">
                                                        <input
                                                            type="checkbox"
                                                            checked={decoration.desktopOnly !== false}
                                                            disabled={readOnly}
                                                            onChange={(e) => setDecoration(index, { desktopOnly: e.target.checked })}
                                                        />
                                                        Desktop only
                                                    </label>
                                                    <label className="flex items-center gap-2 text-[13px] text-gray-700">
                                                        <input
                                                            type="checkbox"
                                                            checked={decoration.anchorRight === true}
                                                            disabled={readOnly}
                                                            onChange={(e) => setDecoration(index, { anchorRight: e.target.checked })}
                                                        />
                                                        Ride with the billboard
                                                    </label>
                                                    {!readOnly && (
                                                        <button
                                                            type="button"
                                                            onClick={() => removeDecoration(index)}
                                                            className="text-[12px] text-red-600 hover:underline"
                                                        >
                                                            remove
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                </>
                                )}
                            </Panel>
                            )}

                            <Panel title="Default artwork">
                                <p className="mb-2 text-[13px] text-gray-700">
                                    The stock {tab === 'askfrank' ? 'AskFrank' : 'website'} art. Save it, edit it, then upload the
                                    result as a new theme's assets.
                                </p>
                                <div className="flex flex-col gap-1">
                                    {downloads.map(url => (
                                        <a
                                            key={url}
                                            href={resolveThemeAssetUrl(url)}
                                            download
                                            className="flex items-center gap-2 rounded bg-gray-100 px-2 py-1 hover:bg-gray-200"
                                        >
                                            <span
                                                className="h-[30px] w-[44px] shrink-0 border border-black/20 bg-white"
                                                style={{
                                                    backgroundImage: `url(${resolveThemeAssetUrl(url)})`,
                                                    backgroundSize: 'contain',
                                                    backgroundPosition: 'center',
                                                    backgroundRepeat: 'no-repeat',
                                                    imageRendering: 'pixelated'
                                                }}
                                            />
                                            <span className="min-w-0 flex-1 truncate text-[12px] text-gray-700">{url}</span>
                                            <span className="shrink-0 font-['Jersey_10'] text-[13px] uppercase tracking-[1px] text-maroon">
                                                Download
                                            </span>
                                        </a>
                                    ))}
                                </div>
                            </Panel>
                        </div>
                    </div>

                    <div className="flex flex-col gap-3 xl:col-span-5">
                        {readOnly && (
                            <div className="border border-gold bg-[#fff8e6] px-3 py-2 text-[13px] text-gray-800">
                                This is the default theme. It cannot be edited or deleted, so there is always something to fall back to.
                                Use "Duplicate" to start a new theme from it.
                            </div>
                        )}

                        <Panel
                            title="Identity"
                            right={
                                !isCreating && (
                                    <button
                                        type="button"
                                        onClick={() => startNew(themes.find(t => t.id === selectedId))}
                                        className="font-['Jersey_10'] text-[14px] uppercase tracking-[1px] text-gold hover:underline"
                                    >
                                        Duplicate
                                    </button>
                                )
                            }
                        >
                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                <label className="block">
                                    <span className={label}>Name</span>
                                    <input
                                        className={input}
                                        value={form.name}
                                        disabled={readOnly}
                                        onChange={(e) => { setForm(prev => ({ ...prev, name: e.target.value })); setDirty(true); }}
                                        placeholder="Halloween 2027"
                                    />
                                </label>
                                <label className="block">
                                    <span className={label}>Key</span>
                                    <input
                                        className={input}
                                        value={form.key}
                                        disabled={!isCreating}
                                        onChange={(e) => { setForm(prev => ({ ...prev, key: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '') })); setDirty(true); }}
                                        placeholder="hallo27"
                                    />
                                    <span className="mt-1 block text-[11px] text-gray-500">
                                        {isCreating ? 'Lowercase letters and numbers, one word. Cannot be changed later.' : 'Fixed once created.'}
                                    </span>
                                </label>
                            </div>
                        </Panel>

                        {tab === 'website' && (
                        <Panel title="Banner">
                            <div className="flex flex-col gap-3">
                                <div>
                                    <span className={label}>Tile background</span>
                                    <ImageSlot
                                        value={form.config.bannerTile.url}
                                        onChange={(v) => setSlot('bannerTile', { url: v })}
                                        onPick={() => pickFile({ kind: 'bannerTile' })}
                                        uploading={uploadingTo === 'bannerTile'}
                                        disabled={readOnly}
                                        placeholder="/img/red_tile.webp"
                                    />
                                </div>

                                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                                    <NumberField
                                        title="Tile align"
                                        value={form.config.bannerTile.alignOffset}
                                        min={-400}
                                        max={400}
                                        disabled={readOnly}
                                        onChange={(v) => setSlot('bannerTile', { alignOffset: v })}
                                    />
                                    <NumberField
                                        title="Tile Y"
                                        value={form.config.bannerTile.offsetY}
                                        min={-400}
                                        max={400}
                                        disabled={readOnly}
                                        onChange={(v) => setSlot('bannerTile', { offsetY: v })}
                                    />
                                    <NumberField
                                        title="Height"
                                        value={form.config.bannerHeight}
                                        min={80}
                                        max={400}
                                        disabled={readOnly}
                                        onChange={(v) => setConfig({ bannerHeight: v })}
                                    />
                                </div>
                            </div>
                        </Panel>
                        )}

                        {tab === 'website' && (
                        <Panel title="Rare furni colour">
                            <div className="flex items-center gap-2">
                                {readOnly ? (
                                    <span
                                        className="h-[28px] w-[38px] shrink-0 border border-gray-300"
                                        style={{ backgroundColor: form.config.furniHex || '#8b2e2e' }}
                                    />
                                ) : (
                                    <ColorPicker
                                        value={form.config.furniHex || '#8b2e2e'}
                                        onChange={(e) => setConfig({ furniHex: normaliseHex(e.target.value) })}
                                        title="Recolour the banner rares"
                                        className="h-[28px] w-[38px] shrink-0"
                                        icon={<EyedropperIcon />}
                                    />
                                )}
                                <HexField
                                    value={form.config.furniHex}
                                    disabled={readOnly}
                                    onChange={(hex) => setConfig({ furniHex: hex })}
                                />
                                {form.config.furniHex && !readOnly && (
                                    <button
                                        type="button"
                                        onClick={() => setConfig({ furniHex: null })}
                                        className="shrink-0 text-[12px] text-gray-500 hover:text-maroon"
                                    >
                                        reset
                                    </button>
                                )}
                            </div>

                            <p className="mt-2 text-[12px] text-gray-600">
                                Empty leaves the dragon lamp, bulb, ice cream and pillow on their stock maroon. A colour sends all
                                four through the furni recolourer so they match the season.
                            </p>
                        </Panel>
                        )}

                        {tab === 'askfrank' && (
                        <Panel title="AskFrank background grid">
                            <ImageSlot
                                value={form.config.askFrankTile.url}
                                onChange={(v) => setSlot('askFrankTile', { url: v })}
                                onPick={() => pickFile({ kind: 'askFrankTile' })}
                                uploading={uploadingTo === 'askFrankTile'}
                                disabled={readOnly}
                                placeholder={DEFAULT_THEME.config.askFrankTile.url}
                            />
                            <p className="mt-2 text-[12px] text-gray-600">
                                Tiled across the AskFrank landing page and behind the FranklyNet search strip. Separate from the
                                banner tile on purpose, so AskFrank can carry its own look. Check it on the AskFrank preview tab.
                            </p>
                        </Panel>
                        )}

                        {LOGO_SLOTS.filter(slot => slot.tab === tab).map(slot => (
                            <Panel key={slot.key} title={slot.label}>
                                <div className="flex flex-col gap-2">
                                    <ImageSlot
                                        value={form.config[slot.key].url}
                                        onChange={(v) => setSlot(slot.key, { url: v })}
                                        onPick={() => pickFile({ kind: slot.key })}
                                        uploading={uploadingTo === slot.key}
                                        disabled={readOnly}
                                        placeholder={DEFAULT_THEME.config[slot.key].url}
                                    />
                                    <div className="grid grid-cols-2 gap-2">
                                        <NumberField
                                            title="Offset X"
                                            value={form.config[slot.key].offsetX}
                                            min={-400}
                                            max={400}
                                            disabled={readOnly}
                                            onChange={(v) => setSlot(slot.key, { offsetX: v })}
                                        />
                                        <NumberField
                                            title="Offset Y"
                                            value={form.config[slot.key].offsetY}
                                            min={-400}
                                            max={400}
                                            disabled={readOnly}
                                            onChange={(v) => setSlot(slot.key, { offsetY: v })}
                                        />
                                    </div>
                                    <p className="text-[12px] text-gray-600">{slot.hint}</p>
                                </div>
                            </Panel>
                        ))}

                        <div className="flex flex-wrap gap-2">
                            {!readOnly && (
                                <button
                                    type="button"
                                    onClick={save}
                                    disabled={saving}
                                    className={`${btn} bg-maroon text-white disabled:opacity-60`}
                                    style={shadow}
                                >
                                    {saving ? 'Saving...' : isCreating ? 'Create theme' : dirty ? 'Save changes *' : 'Save changes'}
                                </button>
                            )}
                            {!readOnly && !isCreating && (
                                <button
                                    type="button"
                                    onClick={remove}
                                    className={`${btn} bg-red-100 text-red-900 hover:bg-red-200`}
                                    style={shadow}
                                >
                                    Delete
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => { setSelectedId(null); setForm(emptyForm()); setDirty(false); }}
                                className={`${btn} bg-gray-200 text-gray-800 hover:bg-gray-300`}
                                style={shadow}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
                </>
            )}
        </div>
    );
}

export default SiteThemes;
